import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { MegaMenuItem, MenuItem } from 'primeng/api';
import { combineLatest, startWith, map } from 'rxjs';
import { UserDetails } from '#models';
import { UserRole } from '#enums';
import { AuthService } from './auth/auth.service';
import { StoreService } from './store';

interface NavTreeGroupItemType {
  roles: UserRole[];
  items: NavTreeType[];
}
interface NavTreeItemType {
  roles: UserRole[];
  routerLink?: string;
  groups?: Record<string, NavTreeGroupItemType>;
}
type NavTreeType = Record<string, NavTreeItemType>;

@Injectable({
  providedIn: 'root',
})
export class NavbarService {
  private _authService = inject(AuthService);
  private _translateService = inject(TranslateService);
  private _storeService = inject(StoreService);
  private _pssrTitle$ = toObservable(this._storeService.currentPssrTitle);

  private readonly navTree: NavTreeType = {
    pssrs: {
      routerLink: '/pssrs',
      roles: [UserRole.User],
    },
    projects: {
      routerLink: '/projects',
      roles: [UserRole.User],
    },
    actionsItems: {
      routerLink: '/action-items',
      roles: [UserRole.User],
    },
    administration: {
      roles: [UserRole.OrgAdmin, UserRole.BuAdmin, UserRole.SysAdmin],
      groups: {
        hierarchyManagement: {
          roles: [UserRole.OrgAdmin, UserRole.BuAdmin, UserRole.SysAdmin],
          items: [
            {
              businessUnits: { routerLink: '/admin/business-units', roles: [UserRole.BuAdmin, UserRole.SysAdmin] },
              organizations: {
                routerLink: '/admin/organizations',
                roles: [UserRole.OrgAdmin, UserRole.BuAdmin, UserRole.SysAdmin],
              },
              facilityHierarchy: { routerLink: '/admin/areas', roles: [UserRole.BuAdmin, UserRole.SysAdmin] },
            },
          ],
        },
        accessManagement: {
          roles: [UserRole.OrgAdmin, UserRole.BuAdmin, UserRole.SysAdmin],
          items: [
            {
              organizationUsers: {
                routerLink: '/admin/users',
                roles: [UserRole.OrgAdmin, UserRole.BuAdmin, UserRole.SysAdmin],
              },
              buAdmins: { routerLink: '/admin/bu-admins', roles: [UserRole.BuAdmin, UserRole.SysAdmin] },
              userGroups: { routerLink: '/admin/user-groups', roles: [UserRole.BuAdmin, UserRole.SysAdmin] },
              systemAdmins: { routerLink: '/admin/system-admins', roles: [UserRole.SysAdmin] },
            },
          ],
        },
        pssrConfiguration: {
          roles: [UserRole.BuAdmin, UserRole.SysAdmin],
          items: [
            {
              templates: { routerLink: '/admin/checklists', roles: [UserRole.BuAdmin, UserRole.SysAdmin] },
              categories: { routerLink: '/admin/categories', roles: [UserRole.BuAdmin, UserRole.SysAdmin] },
            },
          ],
        },
      },
    },
  };

  getNavigation() {
    return combineLatest([
      this._translateService.onLangChange.pipe(startWith(this._translateService.getCurrentLang())),
      this._authService.userDetails$.pipe(startWith(null)),
      this._pssrTitle$.pipe(startWith('PSSR')),
    ]).pipe(map(([_, userDetails, pssrTitle]) => this._getNavigationItems(userDetails, this.navTree, pssrTitle)));
  }
  private _getNavigationItems(
    userDetails: UserDetails | null,
    navTree: NavTreeType,
    pssrTitle = 'PSSR'
  ): MegaMenuItem[] {
    if (!userDetails) {
      return [];
    }

    const menuItems: MegaMenuItem[] = [];
    for (const [key, navItem] of Object.entries(navTree)) {
      const isAuthorized = this._authService.isAuthorized(navItem.roles);
      if (!isAuthorized) {
        continue;
      }

      const menuItem: MegaMenuItem = {
        label:
          key === 'pssrs'
            ? this._translateService.instant('navigation.pssrs', { pssrTitle })
            : this._parseNavItemLabel(key),
        items: [],
      };

      if (navItem.routerLink) {
        menuItem.routerLink = navItem.routerLink;
      }

      if (navItem.groups) {
        menuItem.items = this._getNavigationGroupedItems(navItem.groups);
      }

      menuItems.push(menuItem);
    }
    return menuItems;
  }

  private _getNavigationGroupedItems(groups: Record<string, NavTreeGroupItemType>) {
    const result: MenuItem[][] = [];
    for (const [groupKey, groupItem] of Object.entries(groups)) {
      const isGroupAuthorized = this._authService.isAuthorized(groupItem.roles);
      if (!isGroupAuthorized) {
        continue;
      }

      const groupMenuItem: MenuItem = {
        label: this._parseNavItemLabel(groupKey),
        items: [],
      };

      for (const item of groupItem.items) {
        const subMenuItems: MenuItem[] = [];
        for (const [subKey, subItem] of Object.entries(item)) {
          const isSubItemAuthorized = this._authService.isAuthorized(subItem.roles);
          if (!isSubItemAuthorized) {
            continue;
          }

          subMenuItems.push({
            label: this._parseNavItemLabel(subKey),
            routerLink: subItem.routerLink,
          });
        }
        groupMenuItem.items?.push(...subMenuItems);
      }

      result?.push([groupMenuItem]);
    }
    return result;
  }

  private _parseNavItemLabel(key: string): string {
    return this._translateService.instant('navigation.' + key);
  }
}
